import { useEffect, useState } from "react";
import { getGoogleDrivePickerAccessToken } from "../../../../services/socialTokenService";
import { isGoogleDriveAttachmentsConfigured } from "./googleDriveEnv";

/**
 * Live Drive connection status for the Connect tab.
 *
 * Deliberately probes WITHOUT a profile_id: the backend then lists this customer's
 * stored Drive secrets, so the answer describes the *account* rather than whatever
 * this browser happens to remember in localStorage (which is what the chat menu
 * uses, and why it is wrong on a second device). Cost: one token refresh per load;
 * the minted access token is discarded.
 *
 * A failed probe reports "not connected" and never throws — the Connect tab has
 * eight other cards to render and a status check must not take them down with it.
 */
export function useGoogleDriveConnectionStatus(options: {
  customerId: string | null | undefined;
  idToken: string | null | undefined;
}): { connected: boolean; loading: boolean } {
  const { customerId, idToken } = options;
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!customerId || !idToken || !isGoogleDriveAttachmentsConfigured()) {
      setConnected(false);
      setLoading(false);
      return;
    }
    let active = true;
    setLoading(true);
    getGoogleDrivePickerAccessToken(customerId, idToken)
      .then((result) => {
        if (active) setConnected(result !== null);
      })
      .catch(() => {
        if (active) setConnected(false);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [customerId, idToken]);

  return { connected, loading };
}
